import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { userAtom } from "../store/user";

export function useCalendar() {
  const user = useRecoilValue(userAtom);
  const [schedules, setSchedules] = useState([]);
  const [pivot, setPivot] = useState(moment().startOf("month"));
  const [open, setOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleByDate, setScheduleByDate] = useState(null);

  const handleClickOpen = useCallback((date, s) => {
    setScheduleDate(date);
    setScheduleData(s);
    setScheduleByDate(scheduleMap.get(date));
    setOpen(true);
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const handleThisMonth = () => {
    setPivot(moment().startOf("month").clone());
  };

  const handleNextMonth = () => {
    setPivot((prev) => prev.startOf("month").add("month", 1).clone());
  };

  const handlePrevMonth = () => {
    setPivot((prev) => prev.startOf("month").add("month", -1).clone());
  };

  const scheduleMap = useMemo(() => {
    const map = new Map();
    const byDate = Map.groupBy(schedules, (e) => e.date);
    byDate.forEach((v, date) => {
      const scheduleLines = v.map((s) => ({
        id: s.id,
        updated_at: s.updated_at,
        start_time: (
          s.start_time ?? user.department.start_work_time.substring(0, 5)
        ).substring(0, 5),
        end_time: (
          s.end_time ?? user.department.end_work_time.substring(0, 5)
        ).substring(0, 5),
        is_all_day: s.is_all_day,
        is_negative: s.is_negative,
        is_task: s.source === 3,
        name: s.user.last_name + s.user.first_name,
        schedule_type_id: s.source === 3 ? 0 : s.schedule_type.id,
        schedule_type_order: s.source === 3 ? 0 : s.schedule_type.display_order,
        working_hours: s.working_hours,
        source: s.source,
        is_settled: s.is_settled,
        note: s.note,
        bg_color: s.schedule_type.bg_color,
        fg_color: s.schedule_type.fg_color,
        user_id: s.user.id,
      }));

      scheduleLines.sort((a, b) => {
        if (!a.is_negative && b.is_negative) return -1;
        if (a.is_negative && !b.is_negative) return 1;
        if (a.is_negative && b.is_negative)
          return a.schedule_type_order - b.schedule_type_order;
        if (
          a.start_time < user.department.start_work_time.substring(0, 5) &&
          b.start_time >= user.department.start_work_time.substring(0, 5)
        )
          return 1;
        if (
          a.start_time >= user.department.start_work_time.substring(0, 5) &&
          b.start_time < user.department.start_work_time.substring(0, 5)
        )
          return -1;
        return a.start_time.localeCompare(b.start_time);
      });
      map.set(date, scheduleLines);
    });
    return map;
  }, [schedules, user.department]);

  const canAllDay = (byDate, isNew) => {
    if (!byDate) {
      return true;
    }
    return byDate.length === (isNew ? 0 : 1);
  };

  const regulateTime = (dateString, timeString, isEnd = false) => {
    const time = moment(`${dateString} ${timeString}`);
    const startWorkTime = moment(
      `${dateString} ${user.department.start_work_time}`,
    );
    if (time.isBefore(startWorkTime) || (isEnd && time.isSame(startWorkTime))) {
      time.add(1, "days");
    }
    return time;
  };

  const checkRegister = (target, byDate, exclude) => {
    const date = target.date;
    const target_start_time = regulateTime(date, target.start_time);
    const target_end_time = regulateTime(date, target.end_time, true);
    if (target_start_time.isSameOrAfter(target_end_time)) {
      return "終了時刻は開始時刻以降で指定してください";
    }
    if (!byDate) {
      return null;
    }
    for (const v of byDate) {
      if (!target.is_negative && v === exclude) {
        continue;
      }
      if (
        target.is_negative &&
        v.is_negative &&
        target.schedule_type_id === v.schedule_type_id
      ) {
        return "既に登録されています";
      }
      if (v.is_all_day) {
        return "予定をこれ以上追加できません";
      }
      if (
        target.is_negative &&
        !v.is_negative &&
        target.schedule_type_id === v.schedule_type_id
      ) {
        return "既に登録されている種別を割当て不可にできません。";
      }
      if (
        !target.is_negative &&
        v.is_negative &&
        target.schedule_type_id === v.schedule_type_id
      ) {
        return "既に割当て不可として登録されています。";
      }
      const start_time = regulateTime(date, v.start_time);
      const end_time = regulateTime(date, v.end_time, true);
      if (
        target_start_time.isBefore(end_time) &&
        target_end_time.isAfter(start_time)
      ) {
        return "他の予定と時間が重複しています";
      }
    }
    return null;
  };

  return {
    setSchedules,
    pivot,
    open,
    scheduleData,
    scheduleDate,
    scheduleByDate,
    scheduleMap,
    handleClickOpen,
    handleClose,
    handleThisMonth,
    handleNextMonth,
    handlePrevMonth,
    canAllDay,
    checkRegister,
  };
}
